import React from 'react';
import { Box, Flex, Heading, HStack, Icon, Text } from '@chakra-ui/react';
import { Reveal, Sheet } from '../motion';

export { default as PageHero } from './PageHero';

/**
 * The premium kit: the handful of pieces every section is built from, so the
 * whole site shares one visual language instead of each page improvising.
 *
 * The rules the kit encodes, briefly:
 * - Headings are weight 600, ink on light and white on dark. Never gradient
 *   text; gradient-filled type is the most common template tell there is.
 * - Brand cyan is an accent (a dot, a hairline, an icon stroke), not a fill.
 * - Dark sections are deep teal ink, not cyan-to-blue gradients.
 * - Nothing glows.
 */

/**
 * Section heading with an optional eyebrow and lede.
 *
 * The eyebrow is small tracked capitals led by a cyan dot, which carries the
 * brand without painting the heading. `title` and `lede` accept any node, so
 * existing heading content can be passed through exactly as it was written.
 */
export const SectionHeading = ({
  eyebrow,
  title,
  lede,
  align = 'center',
  tone = 'light',
  as = 'h2',
  size = { base: 'xl', md: '2xl' },
  maxW = '3xl',
  mb = { base: 10, md: 14 },
  ...props
}) => {
  const dark = tone === 'dark';
  const centered = align === 'center';

  return (
    <Reveal textAlign={align} maxW={maxW} mx={centered ? 'auto' : undefined} mb={mb} {...props}>
      {eyebrow && (
        <HStack spacing={2.5} justify={centered ? 'center' : 'flex-start'} mb={4}>
          <Box w="6px" h="6px" borderRadius="full" bg={dark ? 'starOfLife.300' : 'starOfLife.500'} />
          <Text
            fontSize="xs"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.18em"
            // starOfLife.700, not brand.600: the lighter teal is about 3.4:1 on
            // white, which fails AA for text this small. This deep teal is
            // about 6.4:1 and still reads as the brand.
            color={dark ? 'whiteAlpha.800' : 'starOfLife.700'}
          >
            {eyebrow}
          </Text>
        </HStack>
      )}
      <Heading
        as={as}
        size={size}
        fontWeight="600"
        letterSpacing="-0.022em"
        lineHeight="1.15"
        color={dark ? 'white' : 'ink.900'}
      >
        {title}
      </Heading>
      {lede && (
        <Text
          mt={5}
          fontSize={{ base: 'md', md: 'lg' }}
          lineHeight="1.7"
          color={dark ? 'whiteAlpha.800' : 'ink.600'}
          maxW="2xl"
          mx={centered ? 'auto' : undefined}
        >
          {lede}
        </Text>
      )}
    </Reveal>
  );
};

/**
 * An icon in a hairline ring. Replaces the filled, shadowed gradient tiles.
 */
export const IconMark = ({ icon, size = 'md', tone = 'light', ...props }) => {
  const dark = tone === 'dark';
  const box = size === 'lg' ? '60px' : size === 'sm' ? '40px' : '52px';
  const glyph = size === 'lg' ? 6 : size === 'sm' ? 4 : 5;
  return (
    <Flex
      w={box}
      h={box}
      flexShrink={0}
      borderRadius="full"
      align="center"
      justify="center"
      bg={dark ? 'rgba(255,255,255,0.06)' : 'brand.50'}
      border="1px solid"
      borderColor={dark ? 'rgba(255,255,255,0.16)' : 'rgba(14,47,58,0.08)'}
      {...props}
    >
      <Icon as={icon} boxSize={glyph} color={dark ? 'starOfLife.300' : 'brand.600'} />
    </Flex>
  );
};

/**
 * A dark band that slides up over the section before it as a rounded sheet.
 *
 * Replaces every full-bleed cyan-to-blue gradient section. Deep teal ink with,
 * optionally, a facility photograph far in the background, desaturated and
 * held under a scrim so it reads as texture and depth rather than as an image
 * competing with the content. Glass cards (`clearGlass`) sit well on it.
 *
 * Because it is a Sheet it overlaps the previous section slightly and settles
 * into place as it scrolls in.
 */
export const InkBand = ({ image, imageOpacity = 0.2, children, py = { base: 20, md: 28 }, ...props }) => (
  <Sheet bg="brand.900" color="white" overflow="hidden" {...props}>
    <Box position="relative" py={py}>
      {image && (
        <Box
          position="absolute"
          inset={0}
          bgImage={`url('${image}')`}
          bgSize="cover"
          bgPosition="center"
          opacity={imageOpacity}
          filter="grayscale(40%)"
          pointerEvents="none"
        />
      )}
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(180deg, rgba(14,47,58,0.35) 0%, rgba(14,47,58,0.9) 100%)"
        pointerEvents="none"
      />
      <Box position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  </Sheet>
);

/**
 * A light section that slides up over the one before it.
 * `tone="mist"` gives the quiet alternating surface.
 */
export const LightSheet = ({ tone = 'white', children, py = { base: 20, md: 28 }, ...props }) => (
  <Sheet bg={tone === 'mist' ? 'mist.50' : 'white'} {...props}>
    <Box py={py}>{children}</Box>
  </Sheet>
);
